import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, getDoc, doc, query, orderBy, where } from 'firebase/firestore';
import { X, Plus, ChevronDown, ChevronUp, Star, Clock, Film, Flame, Award, Tv, ArrowLeft, ChevronLeft, ChevronRight, Edit, User, MoreHorizontal } from 'lucide-react';
import CreateEpisodeModal from '../modals/CreateEpisodeModal';
import EditShowModal from '../modals/EditShowModal';
import { getFunctions, httpsCallable } from 'firebase/functions';
import EditEpisodeModal from '../modals/EditEpisodeModal';

const ShowDesktop = ({ user, showId, onClose }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [show, setShow] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [selectedSeason, setSelectedSeason] = useState(1);
  const [showSeasonSelector, setShowSeasonSelector] = useState(false);
  const [isUserShow, setIsUserShow] = useState(false);
  const [showCreateEpisodeModal, setShowCreateEpisodeModal] = useState(false);
  const [expandedEpisodeId, setExpandedEpisodeId] = useState(null);
  const [expandedEpisodeSceneIndex, setExpandedEpisodeSceneIndex] = useState(0);
  const [showingCover, setShowingCover] = useState(true);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCastSeason, setSelectedCastSeason] = useState(1);
  const [showCastSeasonSelector, setShowCastSeasonSelector] = useState(false);
  const [showEditEpisodeModal, setShowEditEpisodeModal] = useState(false);
  const [selectedEpisodeForEdit, setSelectedEpisodeForEdit] = useState(null);
  const [showEpisodeMenu, setShowEpisodeMenu] = useState(null);
  
  useEffect(() => {
    const fetchShowData = async () => {
      if (!showId || !user) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Get show document
        const showDoc = await getDoc(doc(db, 'shows', showId));
        
        if (!showDoc.exists()) {
          setError('Show not found');
          setLoading(false);
          return;
        }
        
        const showData = { id: showDoc.id, ...showDoc.data() };
        setShow(showData);
        
        // Check if this is the user's show
        const userShowRef = doc(db, 'users', user.uid, 'shows', showId);
        const userShowDoc = await getDoc(userShowRef);
        setIsUserShow(userShowDoc.exists());
        
        // Get episodes
        const episodesRef = collection(db, 'shows', showId, 'episodes');
        // First get by date ascending (oldest first)
        const episodesQuery = query(episodesRef, orderBy('episodeDate', 'asc'));
        const episodesSnapshot = await getDocs(episodesQuery);
        
        const episodesData = episodesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        console.log('Fetched episodes:', episodesData);
        setEpisodes(episodesData);
        
        // Extract seasons
        const uniqueSeasons = [...new Set(episodesData.map(ep => ep.episodeSeason || 1))];
        const sortedSeasons = uniqueSeasons.sort((a, b) => a - b); // Sort in ascending order
        setSeasons(sortedSeasons);
        
        // Set selected season to the latest season by default
        if (showData.currentSeason) {
          setSelectedSeason(showData.currentSeason);
        } else if (sortedSeasons.length > 0) {
          setSelectedSeason(Math.max(...sortedSeasons)); // Start with latest season
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching show data:', err);
        setError('Failed to load show data');
        setLoading(false);
      }
    };
    
    fetchShowData();
  }, [showId, user]);
  
  useEffect(() => {
    if (show && show.cast) {
      const seasons = Object.keys(show.cast).map(s => parseInt(s));
      if (seasons.length > 0) {
        setSelectedCastSeason(Math.max(...seasons));
      }
    }
  }, [show]);
  
  // Group cast members into rows of 3
  const renderCast = (season) => {
    const cast = show.showCastSeason?.[season] || [];
    const rows = [];
    
    for (let i = 0; i < cast.length; i += 3) {
      const rowMembers = cast.slice(i, i + 3);
      rows.push(
        <div key={`row-${i}`} style={{
          display: 'flex',
          gap: '0.5rem',
          marginBottom: '0.5rem'
        }}>
          {rowMembers.map((member, index) => (
            <div key={index} style={{
              flex: 1,
              backgroundColor: '#333',
              padding: '0.5rem',
              borderRadius: '0.25rem',
              fontSize: '0.85rem'
            }}>
              {member}
            </div>
          ))}
        </div>
      );
    }
    
    return rows;
  };
  
  // Update the episode rating display to use the correct fields
  const getEpisodeRating = (episode) => {
    // Check if we have the new rating format
    if (episode.episodeTotalScore !== undefined && episode.episodeMaximumPossibleScore > 0) {
      return ((episode.episodeTotalScore / episode.episodeMaximumPossibleScore) * 10).toFixed(1);
    }
    // Fall back to the old format
    else if (episode.episodeEarnedScore !== undefined && episode.episodePossibleScore > 0) {
      return ((episode.episodeEarnedScore / episode.episodePossibleScore) * 10).toFixed(1);
    }
    // No rating
    return 'NR';
  };
  
  // Update the show rating display to use the correct fields
  const getShowRating = (show) => {
    if (show.showEarnedScore !== undefined && show.showPossibleScore > 0) {
      return ((show.showEarnedScore / show.showPossibleScore) * 10).toFixed(1);
    }
    return 'NR';
  };
  
  // Get season rating
  const getSeasonRating = (show, season) => {
    if (show.seasonScores && 
        show.seasonScores[season] && 
        show.seasonScores[season].seasonPossibleScore > 0) {
      return ((show.seasonScores[season].seasonEarnedScore / 
               show.seasonScores[season].seasonPossibleScore) * 10).toFixed(1);
    }
    return 'NR';
  };
  
  // Add a function to toggle episode expansion
  const toggleEpisodeExpansion = (e, episodeId) => {
    e.stopPropagation();
    setExpandedEpisodeId(expandedEpisodeId === episodeId ? null : episodeId);
  };
  
  // Helper function to format date
  const formatDate = (dateObj) => {
    if (!dateObj) return 'No date';
    
    if (dateObj.toDate) {
      const date = dateObj.toDate();
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }
    
    return 'Invalid date';
  };
  
  // Update the handleExpandedSceneNavigation function to include cover in navigation
  const handleExpandedSceneNavigation = (e, episode, direction) => {
    e.stopPropagation();
    
    if (!episode.episodeScenes || episode.episodeScenes.length === 0) {
      return;
    }
    
    if (direction === 'prev') {
      if (showingCover) {
        // If on cover, go to last scene
        setShowingCover(false);
        setExpandedEpisodeSceneIndex(episode.episodeScenes.length - 1);
      } else if (expandedEpisodeSceneIndex === 0) {
        // If on first scene, go to cover
        setShowingCover(true);
      } else {
        // Otherwise go to previous scene
        setExpandedEpisodeSceneIndex(prev => prev - 1);
      }
    } else {
      if (showingCover) {
        // If on cover, go to first scene
        setShowingCover(false);
        setExpandedEpisodeSceneIndex(0);
      } else if (expandedEpisodeSceneIndex === episode.episodeScenes.length - 1) {
        // If on last scene, go to cover
        setShowingCover(true);
      } else {
        // Otherwise go to next scene
        setExpandedEpisodeSceneIndex(prev => prev + 1);
      }
    }
  };

  // Reset to show cover when changing expanded episode
  useEffect(() => {
    setShowingCover(true);
    setExpandedEpisodeSceneIndex(0);
  }, [expandedEpisodeId]);
  
  // Add this helper function near your other utility functions
  const isWithinDays = (date, days) => {
    if (!date) return false;
    const dateObj = date.toDate ? date.toDate() : date;
    const now = new Date();
    const diffTime = Math.abs(now - dateObj);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= days;
  };

  // Add this function to get the show's year range
  const getShowYearRange = (episodes) => {
    if (!episodes || episodes.length === 0) return '';
    
    // Get dates and convert to JavaScript Date objects
    const dates = episodes
      .map(ep => ep.episodeDate || ep.episodeCreationDate)
      .filter(date => date) // Filter out undefined dates
      .map(date => date.toDate ? date.toDate() : date);
    
    if (dates.length === 0) return '';
    
    // Sort dates and get oldest and newest
    dates.sort((a, b) => a - b);
    const oldestYear = dates[0].getFullYear();
    const newestYear = dates[dates.length - 1].getFullYear();
    
    // Return year or year range
    return oldestYear === newestYear ? 
      `${oldestYear}` : 
      `${oldestYear} - ${newestYear}`;
  };

  // Add this function to check if a season is new
  const isNewSeason = (episodes, seasonNumber) => {
    if (!episodes || episodes.length === 0) return false;
    
    // Get episodes from this season
    const seasonEpisodes = episodes.filter(ep => (ep.episodeSeason || 1) === seasonNumber);
    if (seasonEpisodes.length === 0) return false;
    
    // Sort by date
    const sortedEpisodes = [...seasonEpisodes].sort((a, b) => {
      const dateA = a.episodeDate || a.episodeCreationDate;
      const dateB = b.episodeDate || b.episodeCreationDate;
      if (!dateA) return 1;
      if (!dateB) return -1;
      return dateB.toDate() - dateA.toDate();
    });
    
    // Check if newest episode is within last 7 days
    const newestEpisode = sortedEpisodes[0];
    const newestDate = newestEpisode.episodeDate || newestEpisode.episodeCreationDate;
    return isWithinDays(newestDate, 7);
  };
  
  // Add a scroll event listener to track the scroll position
  useEffect(() => {
    const handleScroll = (e) => {
      const scrollTop = e.target.scrollTop;
      setScrollPosition(scrollTop);
    };
    
    // Find the scrollable container
    const contentContainer = document.querySelector('.show-content-area');
    if (contentContainer) {
      contentContainer.addEventListener('scroll', handleScroll);
      
      // Cleanup
      return () => {
        contentContainer.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);
  
  // Add this near the top of your component, before the return statement
  useEffect(() => {
    // Add custom scrollbar styles
    const style = document.createElement('style');
    style.textContent = `
      .show-content-area::-webkit-scrollbar {
        width: 8px;
      }
      .show-content-area::-webkit-scrollbar-track {
        background: #333;
        border-radius: 4px;
      }
      .show-content-area::-webkit-scrollbar-thumb {
        background: #6600CC;
        border-radius: 4px;
      }
      .show-content-area::-webkit-scrollbar-thumb:hover {
        background: #7700EE;
      }
    `;
    document.head.appendChild(style);
    
    // Clean up
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Add this function to check if a show is new (created within the last week)
  const isNewShow = (show) => {
    if (!show || !show.showCreationDate) return false;
    return isWithinDays(show.showCreationDate, 7);
  };
  
  // First, let's add a function to get the season date range
  const getSeasonDateRange = (episodes, seasonNumber) => {
    if (!episodes || episodes.length === 0) return '';
    
    // Filter episodes for this season
    const seasonEpisodes = episodes.filter(ep => (ep.episodeSeason || 1) === seasonNumber);
    if (seasonEpisodes.length === 0) return '';
    
    // Get dates and convert to JavaScript Date objects
    const dates = seasonEpisodes
      .map(ep => ep.episodeDate || ep.episodeCreationDate)
      .filter(date => date) // Filter out undefined dates
      .map(date => date.toDate ? date.toDate() : date);
    
    if (dates.length === 0) return '';
    
    // Sort dates and get oldest and newest
    dates.sort((a, b) => a - b);
    const oldestDate = dates[0];
    const newestDate = dates[dates.length - 1];
    
    // Format to show month and year
    const formatMonthYear = (date) => {
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    };
    
    // Return month-year range
    const oldestFormatted = formatMonthYear(oldestDate);
    const newestFormatted = formatMonthYear(newestDate);
    
    return oldestFormatted === newestFormatted ? 
      oldestFormatted : 
      `${oldestFormatted} - ${newestFormatted}`;
  };
  
  // Update the EditShowModal call to handle the updated data
  const handleEditModalClose = async (updatedShowData) => {
    setShowEditModal(false);
    
    if (!updatedShowData) {
      // If no data was returned, just close the modal without updating
      return;
    }
    
    try {
      // Check if the show has collaborators
      const hasCollaborators = show.collaborators && show.collaborators.length > 0;
      
      if (hasCollaborators && updatedShowData.showName !== show.showName) {
        try {
          // Call the Firebase function to update the show for all collaborators
          const functions = getFunctions();
          const editShowWithCollaborators = httpsCallable(functions, 'editShowWithCollaborators');
          
          await editShowWithCollaborators({
            showId: showId,
            showName: updatedShowData.showName,
            collaborators: show.collaborators
          });
          
          console.log('Successfully updated show for all collaborators');
        } catch (error) {
          console.error('Error updating show for collaborators:', error);
        }
      }
      
      // Make sure we preserve all the necessary fields
      const mergedShowData = {
        ...show,
        ...updatedShowData,
        // Ensure these critical fields are preserved
        id: showId,
        episodes: show.episodes
      };
      
      console.log('Updating show with:', mergedShowData);
      
      // Update the show state with the new data
      setShow(mergedShowData);
      
      // If the current season changed, update the selected season
      if (updatedShowData.currentSeason && updatedShowData.currentSeason !== selectedSeason) {
        setSelectedSeason(updatedShowData.currentSeason);
      }
    } catch (err) {
      console.error('Error handling updated show data:', err);
    }
  };
  
  // Add this useEffect to create the loader style
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Custom loader animation */
      .loader {
        position: relative;
        width: 100px;
        height: 100px;
      }
      
      .loader:before, .loader:after {
        content: '';
        border-radius: 50%;
        position: absolute;
        inset: 0;
        box-shadow: 0 0 10px 2px rgba(0, 0, 0, 0.3) inset;
      }
      
      .loader:after {
        box-shadow: 0 2px 0 #6600cc inset;
        animation: rotate 2s linear infinite;
      }
      
      @keyframes rotate {
        0% { transform: rotate(0) }
        100% { transform: rotate(360deg) }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  
  // Add this function to handle editing an episode
  const handleEditEpisode = (e, episode) => {
    e.stopPropagation();
    setSelectedEpisodeForEdit(episode);
    setShowEditEpisodeModal(true);
  };

  // Add this function to handle closing the edit modal
  const handleEditEpisodeModalClose = (updated) => {
    setShowEditEpisodeModal(false);
    setSelectedEpisodeForEdit(null);
    
    // If the episode was updated, refresh the episodes list
    if (updated) {
      // Refresh episodes data
      const fetchEpisodes = async () => {
        try {
          const episodesRef = collection(db, 'shows', showId, 'episodes');
          const episodesQuery = query(episodesRef, orderBy('episodeDate', 'asc'));
          const episodesSnapshot = await getDocs(episodesQuery);
          
          const episodesData = episodesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          setEpisodes(episodesData);
        } catch (err) {
          console.error('Error refreshing episodes:', err);
        }
      };
      
      fetchEpisodes();
    }
  };
  


  // Add this function to handle the edit option click
  const handleEditOption = (e, episode) => {
    e.stopPropagation();
    setShowEpisodeMenu(null);
    setSelectedEpisodeForEdit(episode);
    setShowEditEpisodeModal(true);
  };
  
  if (loading) {
    return (
      <div className="show-modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(5px)',
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <span className="loader"></span>
      </div>
    );
  }
  
  if (error || !show) {
    return (
      <div className="show-modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(5px)',
        zIndex: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <p style={{ color: '#E50914' }}>{error || 'Show not found'}</p>
        <button 
          onClick={onClose}
          style={{
            marginTop: '1rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#6600CC',
            color: 'white',
            border: 'none',
            borderRadius: '0.25rem',
            cursor: 'pointer'
          }}
        >
          Close
        </button>
      </div>
    );
  }
  
  // Filter episodes by selected season
  const seasonEpisodes = episodes.filter(ep => (ep.episodeSeason || 1) === selectedSeason);
  
  return (
    <div className="show-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      backdropFilter: 'blur(5px)',
      zIndex: 5,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '1rem'
    }}>
      <div className="show-modal-content" style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '0.75rem',
        width: '90%',
        maxWidth: '1000px',
        maxHeight: '85vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        margin: 'auto',
        marginTop: '4rem',
        boxShadow: '0 0 30px rgba(102, 0, 204, 0.3)'
      }}>
        {/* Show header with background image */}
        <div style={{
          position: 'relative',
          width: '100%',
          height: '320px',
          backgroundColor: '#333',
          flexShrink: 0,
          transform: `translateY(${Math.min(scrollPosition * 0.2, 40)}px)`,
          transition: 'transform 0.1s ease-out'
        }}>
          {/* Background image with gradient overlay */}
          {show.showImage && (
            <>
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: `url(${show.showImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.7
              }} />
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(26,26,26,1) 100%)'
              }} />
            </>
          )}
          
          {/* Status tags - positioned at the top */}
          <div style={{
            position: 'absolute',
            top: '1rem',
            left: '1rem',
            display: 'flex',
            gap: '0rem',
            zIndex: 10
          }}>
            {/* Streak tag */}
            {show.showStreak > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '.4rem', 
                fontSize: '1rem',
                background: '#6600cc',
                padding: '.3rem 1rem',
                fontWeight: '600',
                color: 'white',
                marginRight: '-0.5rem',
                zIndex: 13,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                <Flame size={16} style={{ marginRight: '0.25rem' }} />
                {show.showStreak} Day Streak
              </div>
            )}
            
            {/* New Show tag */}
            {isNewShow(show) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '.4rem', 
                fontSize: '1rem',
                background: 'white',
                padding: '.3rem 1rem',
                paddingLeft: show.showStreak > 0 ? '1.5rem' : '1rem',
                fontWeight: '600',
                color: '#6600cc',
                marginRight: '-0.5rem',
                zIndex: 12,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                New Release
              </div>
            )}
            
            {/* New Season tag */}
            {isNewSeason(episodes, selectedSeason) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '.4rem', 
                fontSize: '1rem',
                background: 'lightgrey',
                padding: '.3rem 1rem',
                paddingLeft: (show.showStreak > 0 || isNewShow(show)) ? '1.5rem' : '1rem',
                fontWeight: '600',
                color: 'black',
                zIndex: 11,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }}>
                New Season
              </div>
            )}
          </div>
          
          {/* Close button */}
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              zIndex: 10
            }}
          >
            <X size={20} />
          </button>
          
          {/* Show info - reorganized */}
          <div style={{
            position: 'absolute',
            bottom: '0rem',
            left: '1.5rem',
            right: '1.5rem',
            color: 'white',
            zIndex: 5
          }}>
            <h1 style={{
              margin: '0 0 0.75rem 0',
              fontSize: '2.25rem',
              fontWeight: 'bold'
            }}>
              {show.showName}
            </h1>
            
            {/* First row: Year range, Season/Episodes count, Created by */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1.5rem',
              flexWrap: 'wrap',
              marginBottom: '0.75rem'
            }}>
              {/* Year range */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}>
                <span>{getShowYearRange(episodes)}</span>
              </div>
              
              {/* Season count */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                color: 'lightgrey'
              }}>
                <span>{seasons.length} {seasons.length === 1 ? 'Season' : 'Seasons'} - </span>
                <span>{episodes.length} Episodes</span>
              </div>
              
              {/* Creator info */}
              {show.showCreator && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem',
                  color: 'lightgrey'
                }}>
                  <span>Created by <strong>{show.showCollaborators && show.showCollaborators[show.showCreator] ? (
                    <span style={{ color: '#ccc' }}>
                      {show.showCollaborators[show.showCreator].name || 'Unknown Creator'}
                    </span>
                  ) : (
                    <span style={{ color: '#ccc' }}>Unknown Creator</span>
                  )}</strong></span>
                </div>
              )}
              
              {/* Rating */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                fontSize: '.8rem',
                border: '1px solid grey',
                padding: '.3rem .3rem',
                height: '.7rem',
                borderRadius: '3px',
                color: '#999',
                fontWeight: 'bold'
              }}>
                ISDb: {getShowRating(show)}
              </div>

              {isUserShow && (
           <>
                <button
                  onClick={() => setShowEditModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    
                    marginLeft: 'auto',
                    backgroundColor: 'transparent',
                    color: 'grey',
                    border: 'none',
                    padding: '0.5rem',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    marginTop: '0rem'
                  }}
                >
                  <Edit size={16} />
                  Edit Show
                </button>
            
                <button
                  onClick={() => setShowCreateEpisodeModal(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginLeft: '1rem',
                    backgroundColor: '#6600CC',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    padding: '0.5rem 0.75rem',
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  <Plus size={14} />
                  Create Episode
                </button>
                </>
            )}

            </div>
            
            {/* Second row: Create Episode button (if user's show) */}
          
          </div>
        </div>
        
        {/* Main content area */}
        <div 
          className="show-content-area"
          style={{
            padding: '1.5rem',
            flex: 1,
            overflowY: 'auto',
            background: 'linear-gradient(to bottom, rgba(26,26,26,0.95) 0%, rgba(26,26,26,1) 5%)',
            scrollbarWidth: 'thin',
            scrollbarColor: '#6600CC #333',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#333',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#6600CC',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#7700EE',
            }
          }}>
          {/* Show description */}
          <div style={{
            display: 'flex',
            gap: '2rem',
            flexWrap: 'wrap',
            marginBottom: '2rem'
          }}>
            {/* About section - left side */}
            <div style={{
              flex: '1 1 50%',
              marginTop: '-1.8rem',
              minWidth: '300px'
            }}>
              <h3 style={{
                fontSize: '.8rem',
                fontWeight: '600',
                color: 'grey',
                marginBottom: '0.1rem'
              }}>
                About this Show
              </h3>
              <p style={{
                margin: 0,
                lineHeight: 1.6,
                color: '#ccc'
              }}>
                {show.showDescription || 'No description available.'}
              </p>
              
          
            </div>
            
            {/* Cast section - right side */}
            {show.showCastSeason && Object.keys(show.showCastSeason).length > 0 && (
              <div style={{
                flex: '1 1 45%',
                minWidth: '250px'
              }}>
                {/* First row with label and first 2 cast members */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '0.5rem',
                  gap: '1rem'
                }}>
                  {/* Cast label with season dropdown */}
                  <div style={{ position: 'relative' }}>
                    <div 
                      onClick={() => setShowCastSeasonSelector(!showCastSeasonSelector)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        cursor: 'pointer',
                        fontSize: '.9rem',
                        fontWeight: 'bold',
                        color: 'white'
                      }}
                    >
                      Cast (Season {selectedCastSeason})
                      {Object.keys(show.showCastSeason).length > 1 && (
                        <ChevronDown size={16} />
                      )}
                    </div>
                    
                    {showCastSeasonSelector && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.25rem',
                        backgroundColor: '#333',
                        borderRadius: '0.25rem',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        zIndex: 20
                      }}>
                        {Object.keys(show.showCastSeason).map((season) => (
                          <div 
                            key={season}
                            onClick={() => {
                              setSelectedCastSeason(parseInt(season));
                              setShowCastSeasonSelector(false);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              cursor: 'pointer',
                              backgroundColor: selectedCastSeason === parseInt(season) ? '#6600CC' : 'transparent',
                              color: 'white',
                              whiteSpace: 'nowrap',
                              fontSize: '0.9rem'
                            }}
                          >
                            Season {season}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* First 2 cast members */}
                  {show.showCastSeason[selectedCastSeason] && 
                   show.showCastSeason[selectedCastSeason].length > 0 && 
                   show.showCastSeason[selectedCastSeason].slice(0, 2).map((castName, index) => (
                    <span
                      key={index}
                      style={{
                        fontSize: '0.9rem',
                        color: '#ccc'
                      }}
                    >
                      {castName}
                    </span>
                  ))}
                </div>
                
                {/* Second row with next 3 cast members */}
                {show.showCastSeason[selectedCastSeason] && 
                 show.showCastSeason[selectedCastSeason].length > 2 && (
                  <div style={{
                    display: 'flex',
                    gap: '1rem',
                    marginBottom: '0.5rem'
                  }}>
                    {show.showCastSeason[selectedCastSeason].slice(2, 5).map((castName, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '0.9rem',
                          color: '#ccc'
                        }}
                      >
                        {castName}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Third row with remaining cast members (if more than 5) */}
                {show.showCastSeason[selectedCastSeason] && 
                 show.showCastSeason[selectedCastSeason].length > 5 && (
                  <div style={{
                    display: 'flex',
                    gap: '1rem'
                  }}>
                    {show.showCastSeason[selectedCastSeason].slice(5).map((castName, index) => (
                      <span
                        key={index}
                        style={{
                          fontSize: '0.9rem',
                          color: '#ccc'
                        }}
                      >
                        {castName}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Season selector and episode controls */}
        
          
          {/* Episodes List - Full width */}
          <div>
            {/* Season header with rating */}
            <div style={{
              marginBottom: '1.5rem',
              display: 'flex',
              marginTop: '-1rem',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative'
            }}>
              {/* Remove the duplicate New Season tag that was here */}
              
              <div>
                <h3 style={{ 
                  margin: '0 0 0.25rem 0', 
                  fontSize: '1.5rem',
                  fontWeight: 'bold'
                }}>
             Episodes
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.9rem',
                  color: '#999'
                }}>
                
                  
                  
                  {/* Episode count */}
                  <span>
                    {episodes.filter(ep => (ep.episodeSeason || 1) === selectedSeason).length} episodes
                  </span>
                  
                 
                  {/* Date range */}
                  <span>{getSeasonDateRange(episodes, selectedSeason)}</span>
                    {/* Rating */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '0.7rem',
                      border: '1px solid grey',
                      padding: '.3rem .3rem',
                      height: '.5rem',
                      borderRadius: '3px',
                      color: '#999',
                      fontWeight: 'bold'
                  }}>
                    ISDb:
                     <span>{getSeasonRating(show, selectedSeason)}</span>
                  </div>
                  
                </div>
                
              </div>
              
              {/* Move Create Episode button to the right with no background */}
        




              <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem'
          }}>
            <div style={{ position: 'relative' }}>
              <div 
                onClick={() => setShowSeasonSelector(!showSeasonSelector)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: '#333',
                  borderRadius: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  color: 'white'
                }}
              >
                Season {selectedSeason}
                <ChevronDown size={16} />
              </div>
              
              {showSeasonSelector && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  marginTop: '0.25rem',
                  backgroundColor: '#333',
                  borderRadius: '0.5rem',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  zIndex: 20
                }}>
                  {seasons.map((season) => (
                    <div 
                      key={season}
                      onClick={() => {
                        setSelectedSeason(season);
                        setShowSeasonSelector(false);
                      }}
                      style={{
                        padding: '0.5rem 1rem',
                        cursor: 'pointer',
                        backgroundColor: selectedSeason === season ? '#6600CC' : 'transparent',
                        color: 'white'
                      }}
                    >
                      Season {season}
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Create Episode button */}
          
          </div>




            </div>
            
            {/* Episodes for selected season */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1.5rem' 
            }}>
              {episodes
                .filter(ep => (ep.episodeSeason || 1) === selectedSeason)
                .map((episode, index) => (
                  <div
                    key={episode.id}
                    style={{
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      marginBottom: '0.5rem', // Additional spacing
                      ':hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                      }
                    }}
                  >
                    {expandedEpisodeId === episode.id ? (
                      // Expanded Episode View
                      <div style={{
                        padding: '1.5rem',
                        backgroundColor: ''
                      }}>
                        <div style={{
                          display: 'flex',
                          gap: '2rem',
                          alignItems: 'flex-start'
                        }}>
                          {/* Left column - Image with scenes */}
                          <div style={{
                            width: '50%',
                            position: 'relative'
                          }}>
                            {/* Episode image or current scene */}
                            <div 
                              onClick={(e) => {
                                e.stopPropagation();
                                if (episode.episodeScenes && episode.episodeScenes.length > 0) {
                                  // If showing cover, go to first scene
                                  if (showingCover) {
                                    console.log('Switching from cover to first scene');
                                    setShowingCover(false);
                                    setExpandedEpisodeSceneIndex(0);
                                  } 
                                  // If showing a scene, go to next scene or back to cover
                                  else {
                                    console.log('Navigating to next scene or cover');
                                    if (expandedEpisodeSceneIndex === episode.episodeScenes.length - 1) {
                                      setShowingCover(true);
                                    } else {
                                      setExpandedEpisodeSceneIndex(prev => prev + 1);
                                    }
                                  }
                                }
                              }}
                              style={{
                                position: 'relative',
                                cursor: episode.episodeScenes && episode.episodeScenes.length > 0 ? 'pointer' : 'default'
                              }}
                            >
                              {showingCover ? (
                                // Cover image in 3:2 aspect ratio
                                episode.episodeImage ? (
                                  <img
                                    src={episode.episodeImage}
                                    alt={episode.episodeTitle}
                                    style={{
                                      width: '100%',
                                      aspectRatio: '3/2',
                                      objectFit: 'cover',
                                      borderRadius: '0.5rem'
                                    }}
                                  />
                                ) : (
                                  <div style={{
                                    width: '100%',
                                    aspectRatio: '3/2',
                                    backgroundColor: '#333',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '0.5rem'
                                  }}>
                                    <Film size={48} color="#666" />
                                  </div>
                                )
                              ) : (
                                // Scene images in their natural aspect ratio but matching height
                                <div style={{
                                  width: '100%',
                                  height: '300px', // Set a consistent height
                                  backgroundColor: '#111',
                                  borderRadius: '0.5rem',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}>
                                  {episode.episodeScenes[expandedEpisodeSceneIndex] ? (
                                    <img
                                      src={episode.episodeScenes[expandedEpisodeSceneIndex]}
                                      alt={`Scene ${expandedEpisodeSceneIndex + 1}`}
                                      style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain'
                                      }}
                                      onError={(e) => {
                                        console.error('Failed to load scene image:', e);
                                        e.target.src = episode.episodeImage || '';
                                        e.target.style.opacity = 0.5;
                                      }}
                                    />
                                  ) : (
                                    <div style={{
                                      color: '#666',
                                      textAlign: 'center'
                                    }}>
                                      <Film size={48} color="#666" />
                                      <div style={{ marginTop: '0.5rem' }}>Scene image not available</div>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {/* Episode number badge */}
                              <div style={{
                                position: 'absolute',
                                top: '0.75rem',
                                left: '0.75rem',
                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                color: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '0.25rem',
                                fontSize: '0.9rem',
                                fontWeight: 'bold'
                              }}>
                                Season {selectedSeason} • Episode {index + 1}
                              </div>
                              
                              {/* Scene navigation controls - show whenever there are scenes */}
                              {episode.episodeScenes && episode.episodeScenes.length > 0 && (
                                <>
                                  {/* Previous button */}
                                  <button
                                    onClick={(e) => handleExpandedSceneNavigation(e, episode, 'prev')}
                                    style={{
                                      position: 'absolute',
                                      left: '0.5rem',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '36px',
                                      height: '36px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <ChevronLeft size={20} />
                                  </button>
                                  
                                  {/* Next button */}
                                  <button
                                    onClick={(e) => handleExpandedSceneNavigation(e, episode, 'next')}
                                    style={{
                                      position: 'absolute',
                                      right: '0.5rem',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '50%',
                                      width: '36px',
                                      height: '36px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <ChevronRight size={20} />
                                  </button>
                                </>
                              )}
                              
                              {/* Scene counter and progress bar - modified to include cover */}
                              {episode.episodeScenes && episode.episodeScenes.length > 0 && (
                                <div style={{
                                  position: 'absolute',
                                  bottom: '0.75rem',
                                  left: '0.75rem',
                                  right: '0.75rem'
                                }}>
                                  {/* Progress bar - include cover as first item */}
                                  <div style={{
                                    display: 'flex',
                                    gap: '0.25rem',
                                    marginBottom: '0.5rem'
                                  }}>
                                    {/* Cover indicator */}
                                    <div
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setShowingCover(true);
                                      }}
                                      style={{
                                        flex: 1,
                                        height: '4px',
                                        // Fill the cover if we're showing it OR if we're showing a scene
                                        // (which means we've passed the cover)
                                        backgroundColor: showingCover || !showingCover ? '#6600CC' : 'rgba(255, 255, 255, 0.3)',
                                        borderRadius: '2px',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s'
                                      }}
                                    />
                                    
                                    {/* Scene indicators */}
                                    {episode.episodeScenes.map((_, idx) => {
                                      // Determine if this scene should be filled
                                      // Fill if: 
                                      // 1. We're showing this scene OR
                                      // 2. We've passed this scene (showing a later scene)
                                      const shouldFill = !showingCover && (idx <= expandedEpisodeSceneIndex);
                                      
                                      return (
                                        <div
                                          key={idx}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowingCover(false);
                                            setExpandedEpisodeSceneIndex(idx);
                                          }}
                                          style={{
                                            flex: 1,
                                            height: '4px',
                                            backgroundColor: shouldFill ? '#6600CC' : 'rgba(255, 255, 255, 0.3)',
                                            borderRadius: '2px',
                                            cursor: 'pointer',
                                            transition: 'background-color 0.2s'
                                          }}
                                        />
                                      );
                                    })}
                                  </div>
                                  
                                  {/* Scene counter */}
                                  <div style={{
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    color: 'white',
                                    padding: '0.25rem 0.5rem',
                                    borderRadius: '0.25rem',
                                    fontSize: '0.8rem',
                                    display: 'inline-block'
                                  }}>
                                    {showingCover 
                                      ? 'Cover' 
                                      : `Scene ${expandedEpisodeSceneIndex + 1} of ${episode.episodeScenes.length}`}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* View all scenes button */}
                            {episode.episodeScenes && episode.episodeScenes.length > 0 && (
                              <div style={{
                                marginTop: '0.75rem',
                                textAlign: 'center',
                                fontSize: '0.9rem',
                                color: '#999'
                              }}>
                                Click image to navigate through scenes
                              </div>
                            )}
                          </div>
                          
                          {/* Right column - Episode details */}
                          <div style={{
                            flex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '1rem'
                          }}>
                            {/* Title and date */}
                            <div>
                              <div style={{display: 'flex'}}>
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  flex: 1
                                }}>
                                  <h3 style={{
                                    margin: '0 0 0.25rem 0',
                                    fontSize: '1.25rem',
                                    fontWeight: 'bold'
                                  }}>
                                    {episode.episodeTitle}
                                  </h3>
                                  
                                  
                                    
                                </div>
                                
                                {/* Date on the right */}
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  fontSize: '0.85rem',
                                  color: '#999',
                                  position: 'relative'
                                }}>
                                  {formatDate(episode.episodeDate || episode.episodeCreationDate)}
                                </div>
                              </div>
                            </div>
                            
                            {/* Stats row */}
                            <div style={{
                              display: 'flex',
                              gap: '1.5rem',
                              padding: '0.75rem 0',
                              borderTop: '1px solid #333',
                              borderBottom: '1px solid #333'
                            }}>
                                 <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontSize: '0.8rem', color: '#999'
                                }}>
                                  {formatDate(episode.episodeDate || episode.episodeCreationDate)}
                                </div>

                              {/* Duration */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                              
                                  <div style={{ fontSize: '0.8rem', color: '#999' }}>Duration:</div>
                                  <div style={{ fontSize: '.8rem', fontWeight: 'bold' , color:'grey'}}>
                                    {episode.episodeDuration || 0}m
                                  </div>
                              </div>
                              
                              {/* Rating */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem'
                              }}>
                                  <div style={{ fontSize: '0.8rem', color: '#999' }}>ISDb:</div>
                                  <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '.8rem',
                                    border: '1px solid grey',
                                    padding: '.3rem .3rem',
                                    height: '.5rem',
                                    lineHeight: '1.2',
                                    textAlign: 'center',
                                    borderRadius: '3px',
                                    color: '#999',
                                    fontWeight: 'bold'
                                  }}>
                                    {getEpisodeRating(episode)}
                                  </div>
                       
                              </div>
                              
                              {/* Scenes count */}
                              {episode.episodeScenes && episode.episodeScenes.length > 0 && (
                                <div style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.5rem'
                                }}>
                                 
                                    <div style={{ fontSize: '0.8rem', color: '#999' }}>Scenes</div>
                                    
                                    <Film size={16} color="grey" style={{marginTop: '3px'}}/>
                                    <div style={{ 
                                      fontSize: '1rem', 
                                      fontWeight: 'bold',
                                      marginLeft: '-4px',
                                  alignItems: 'center',
                                      color: 'grey'
                                    }}>
                           
                                      {episode.episodeScenes.length}
                                    </div>
                            
                                </div>
                              )}
                            </div>
                            
                            {/* Description */}
                            <div>
                              <h4 style={{ 
                                margin: '0 0 0.5rem 0',
                                fontSize: '1rem',
                                color: '#999'
                              }}>
                                Description
                              </h4>
                              <p style={{
                                margin: 0,
                                fontSize: '1rem',
                                color: 'white',
                                lineHeight: 1.6
                              }}>
                                {episode.episodeDescription || 'No description available.'}
                              </p>
                            </div>
                            
                            {/* Add console logs to debug scene navigation */}
                            {console.log('Episode scenes:', episode.episodeScenes)}
                            {console.log('Showing cover:', showingCover)}
                            {console.log('Scene index:', expandedEpisodeSceneIndex)}
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Collapsed Episode View
                      <div 
                        onClick={(e) => toggleEpisodeExpansion(e, episode.id)}
                        style={{
                          display: 'flex',
                          gap: '1rem', 
                          position: 'relative'
                        }}
                      >
                        {/* New Episode tag */}
                        {isWithinDays(episode.episodeDate || episode.episodeCreationDate, 3) && (
                          <div style={{
                            position: 'absolute',
                            top: '0',
                            left: '3rem',
                            backgroundColor: '#6600cc',
                            color: 'white',
                            padding: '0.15rem 0.5rem',
                            borderRadius: '0.25rem',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            zIndex: 20,
                            textTransform: 'uppercase'
                          }}>
                            New
                          </div>
                        )}
                        
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          transform: 'translatey(-50%)',
                          left: '0rem',
                          color: 'grey', 
                          zIndex: '10',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '1rem',
                          fontWeight: 'bold'
                        }}>
                          {index + 1}
                        </div>
                        
                        {/* Episode Image */}
                        <div style={{
                          height: '7rem',
                          
                          marginLeft: '3rem',
                          aspectRatio: '3/2',
                          flexShrink: 0,
                          position: 'relative'
                        }}>
                          {episode.episodeImage ? (
                            <img
                              src={episode.episodeImage}
                              alt={episode.episodeTitle}
                              style={{
                                width: '100%',
                                height: '100%',
                                
                          borderRadius: '.2rem',
                                objectFit: 'cover'
                              }}
                            />
                          ) : (
                            <div style={{
                              width: '100%',
                              height: '100%',
                              backgroundColor: '#333',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <Film size={32} color="#666" />
                            </div>
                          )}
                          
                          {/* Episode number badge */}
                        
                          {/* Scenes indicator on image */}
                         
                        </div>
                        
                        {/* Episode Details */}
                        <div style={{
                          flex: 1,
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}>
                          <div>



                            
                          <div style={{display: 'flex'}}>
                            
                            <h3 style={{
                              margin: '0 0 0.25rem 0',
                              fontSize: '1.25rem',
                              fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center'
                            }}>
                              {episode.episodeTitle}

                              {episode.episodeScenes && episode.episodeScenes.length > 0 && (
                                <Film size={14} style={{color: 'grey', marginLeft: '1rem', }}/>
                              )}
   {/* Rating */}
   <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '0.6rem',
                                margin: '0 .5rem',
                                border: '1px solid grey',
                                padding: '.3rem .3rem',
                                height: '.5rem',
                                borderRadius: '3px',
                                color: getEpisodeRating(episode) !== 'NR' ? '#999' : '#999',
                                fontWeight: 'bold'
                              }}>
                                ISDb: {getEpisodeRating(episode)}
                              </div>


<p style={{fontSize: '.7rem', fontWeight: '600', color: 'grey', marginLeft: '10px'}}>
{episode.episodeDuration || 0}m
</p>
                            </h3>
                            
                            {/* Date, Duration, Rating aligned in a row */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '1rem',
                              marginLeft: 'auto',
                            }}>
                              {/* Date */}
                              <div style={{
                                fontSize: '0.85rem',
                                color: '#999',
                              }}>
                              </div>

                              {/* Duration */}
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '0.85rem',
                                color: '#999'
                              }}>
                              {formatDate(episode.episodeDate || episode.episodeCreationDate)}
                              </div>
                              
                              {/* Rating */}
                             <button 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditOption(e, episode);
                              }} 
                              style={{
                                background: 'transparent',
                                border: 'none',
                                padding: '0.25rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            > 
                              <MoreHorizontal size={20} color="#999" /> 
                            </button>
                            </div>
                          </div>
                            <p style={{
                              margin: 0,
                              fontSize: '0.9rem',
                              color: '#aaa',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              width: '80%',
                              textOverflow: 'ellipsis',
                              lineHeight: 1.5
                            }}>
                              {episode.episodeDescription || 'No description available.'}
                            </p>
                          </div>
                          
                    
                        </div>
                      </div>
                    )}
                  </div>
                ))}
            </div>
            
            {/* Empty state for this season */}
            {episodes.filter(ep => (ep.episodeSeason || 1) === selectedSeason).length === 0 && (
              <div style={{
                padding: '3rem',
                textAlign: 'center',
                color: '#999',
                backgroundColor: '#222',
                borderRadius: '0.5rem'
              }}>
                <p style={{ margin: '0 0 1rem 0', fontSize: '1.1rem' }}>
                  No episodes for Season {selectedSeason}
                </p>
                {isUserShow && (
                  <button
                    onClick={() => setShowCreateEpisodeModal(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      backgroundColor: '#6600CC',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.5rem',
                      padding: '0.75rem 1.25rem',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    <Plus size={16} />
                    Create First Episode
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Create Episode Modal */}
      {showCreateEpisodeModal && (
        <CreateEpisodeModal
          isOpen={showCreateEpisodeModal}
          onClose={() => setShowCreateEpisodeModal(false)}
          showId={showId}
          showName={show.showName}
          userId={user?.uid}
          currentSeason={show.currentSeason || 1}
        />
      )}
      
      {/* Edit Show Modal */}
      {showEditModal && (
        <EditShowModal
          isOpen={showEditModal}
          onClose={handleEditModalClose}
          showId={showId}
          show={show}
        />
      )}
      
      {/* Edit Episode Modal */}
      {showEditEpisodeModal && selectedEpisodeForEdit && (
        <EditEpisodeModal
          isOpen={showEditEpisodeModal}
          onClose={handleEditEpisodeModalClose}
          showId={showId}
          showName={show.showName}
          userId={user?.uid}
          episodeId={selectedEpisodeForEdit.id}
          episode={selectedEpisodeForEdit}
        />
      )}
    </div>
  );
};

export default ShowDesktop; 